import { useEffect, useMemo, useState } from "react";
import { ChevronRight, ChevronLeft, Loader2, Bike, Car } from "lucide-react";
import { api } from "../../lib/api";

const shift = (m, d) => { const [y, mo] = m.split("-").map(Number); const x = new Date(y, mo - 1 + d, 1); return `${x.getFullYear()}-${String(x.getMonth() + 1).padStart(2, "0")}`; };
const label = (m) => { const [y, mo] = m.split("-").map(Number); return new Date(y, mo - 1, 1).toLocaleDateString("ar-SA-u-ca-gregory-nu-latn", { month: "long", year: "numeric" }); };
const WD = ["ن", "ث", "ر", "خ", "ج", "س", "ح"];

const dayInfo = (v, month, d) => {
  const ds = `${month}-${String(d).padStart(2, "0")}`;
  const b = v.bookings.find((x) => x.start_date <= ds && x.to >= ds);
  return { ds, b };
};

const Cell = ({ v, month, d, today, wd }) => {
  const { ds, b } = dayInfo(v, month, d);
  const cls = b ? (b.overdue && ds > b.end_date ? "bg-rose-400" : b.status === "closed" ? "bg-slate-300" : "bg-blue-500")
    : v.maintenance ? "bg-amber-200" : "bg-emerald-50 hover:bg-emerald-100";
  return (
    <td className={`h-8 min-w-[26px] border border-white p-0 ${ds === today ? "ring-2 ring-inset ring-slate-800" : ""} ${wd === 4 ? "border-l-slate-200" : ""}`}
      title={b ? `${ds} · عقد ${b.number} · ${b.renter_name}${b.overdue && ds > b.end_date ? " · متأخر" : ""}` : `${ds} · ${v.maintenance ? "صيانة" : "متاحة"}`}>
      <div className={`h-full w-full ${cls}`} data-testid={`rcal-${v.id}-${d}`} />
    </td>
  );
};

export const RentalCalendarTab = () => {
  const [month, setMonth] = useState(() => new Date(Date.now() + 3 * 3600e3).toISOString().slice(0, 7));
  const [d, setD] = useState(null);
  const [onlyFree, setOnlyFree] = useState(false);
  useEffect(() => { setD(null); api.rentalCalendar(month).then(setD).catch(() => setD({ vehicles: [], days: 30 })); }, [month]);
  const days = useMemo(() => Array.from({ length: d?.days || 0 }, (_, i) => i + 1), [d]);
  const rows = (d?.vehicles || []).filter((v) => !onlyFree || v.free_days > 0);
  return (
    <div className="space-y-3" data-testid="rental-calendar">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-1">
          <button onClick={() => setMonth(shift(month, -1))} className="rounded-lg border border-slate-200 bg-white p-1.5" data-testid="rcal-prev"><ChevronRight className="h-4 w-4" /></button>
          <span className="min-w-[120px] text-center font-heading font-bold text-slate-800" data-testid="rcal-month">{label(month)}</span>
          <button onClick={() => setMonth(shift(month, 1))} className="rounded-lg border border-slate-200 bg-white p-1.5" data-testid="rcal-next"><ChevronLeft className="h-4 w-4" /></button>
        </div>
        <div className="flex flex-wrap items-center gap-3 text-[11px] text-slate-500">
          {[["bg-emerald-100", "متاحة"], ["bg-blue-500", "محجوزة"], ["bg-slate-300", "عقد منتهي"], ["bg-rose-400", "متأخرة"], ["bg-amber-200", "صيانة"]].map(([c, l]) => (
            <span key={l} className="flex items-center gap-1"><span className={`h-3 w-3 rounded ${c}`} />{l}</span>
          ))}
          <label className="flex items-center gap-1 font-semibold text-slate-600"><input type="checkbox" checked={onlyFree} onChange={(e) => setOnlyFree(e.target.checked)} className="h-3.5 w-3.5 accent-emerald-600" data-testid="rcal-only-free" /> اللي فيها أيام فاضية</label>
        </div>
      </div>
      {!d ? <Loader2 className="mx-auto h-6 w-6 animate-spin text-slate-300" /> : !rows.length ? (
        <div className="rounded-2xl border border-dashed border-slate-300 bg-white py-12 text-center text-sm text-slate-400" data-testid="rcal-empty">لا توجد مركبات تأجير</div>
      ) : (
        <div className="overflow-x-auto rounded-2xl border border-slate-200 bg-white">
          <table className="min-w-max border-collapse text-xs">
            <thead><tr className="text-slate-500">
              <th className="sticky right-0 z-10 bg-white px-3 py-2 text-right">المركبة</th>
              {days.map((n) => { const wd = (d.first_weekday + n - 1) % 7; return <th key={n} className={`px-0.5 py-1 text-center font-medium ${wd === 4 || wd === 5 ? "text-rose-400" : ""}`}><div>{n}</div><div className="text-[9px] text-slate-300">{WD[wd]}</div></th>; })}
              <th className="px-2 py-2 text-center">فاضية</th>
            </tr></thead>
            <tbody>{rows.map((v) => { const Icon = v.kind === "moto" ? Bike : Car; return (
              <tr key={v.id} data-testid={`rcal-row-${v.id}`}>
                <td className="sticky right-0 z-10 whitespace-nowrap bg-white px-3 py-1"><span className="flex items-center gap-1.5 font-semibold text-slate-700"><Icon className="h-3.5 w-3.5 text-slate-400" /><span dir="ltr">{v.identifier || "—"}</span></span></td>
                {days.map((n) => <Cell key={n} v={v} month={month} d={n} today={d.today} wd={(d.first_weekday + n - 1) % 7} />)}
                <td className={`px-2 text-center font-bold tabular-nums ${v.free_days ? "text-emerald-600" : "text-slate-400"}`} data-testid={`rcal-free-${v.id}`}>{v.free_days}</td>
              </tr>
            ); })}</tbody>
          </table>
        </div>
      )}
    </div>
  );
};
